import {Injectable} from '@angular/core';
import {CardWeb} from './card-web';
import {BehaviorSubject} from 'rxjs';
import {CardStoreService} from './card-store.service';
import {DeckType, LinkageDirection, LinkageType, PathType} from './card-deck';
import {CardWebElement} from './card-web-element';
import {GameDesignService} from '../design-recommender/shared/game-design.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ObjectExtractingServiceService} from './object-extracting-service.service';
import {LayoutService} from '../graph-editor/shared/layout.service';
import {GameDesignRelation} from '../design-recommender/shared/game-design-relation';
import {MessageFactoryService} from './message-factory.service';

@Injectable({
  providedIn: 'root'
})
export class CardWebService {

  public cardWeb: CardWeb;
  public cw$: BehaviorSubject<CardWeb>;
  private gridSize = 50;
  private locationGrid: boolean[][];
  private connectionPreference: string;
  private connectionPreferencePosX: number;
  private connectionPreferencePosY: number;

  constructor(
    private cs: CardStoreService,
    private ls: LayoutService,
    private gs: GameDesignService,
    private snackBar: MatSnackBar,
    private ccs: ObjectExtractingServiceService,
    private mfs: MessageFactoryService,
  ) {
    this.cs.getUserLoginListener().subscribe(b => {
      if (b === undefined) {
        this.cardWeb = undefined;
      }
    });
    this.initLocationGrid();
  }

  public checkIfCustomCardWebIsInitialized() {
    if (this.cardWeb === undefined) {
      throw new Error('No custom cardweb initialized');
    } else {
      return true;
    }
  }

  public hasCustomCardWeb(): boolean {
    try {
      this.checkIfCustomCardWebIsInitialized();
      return true;
    } catch (e) {
      return false;
    }
  }

  public getCustomCardWeb() {
    this.checkIfCustomCardWebIsInitialized();
    return this.cardWeb;
  }

  public clearCustomCardWeb() {
    this.cardWeb.cards.splice(0, this.cardWeb.cards.length);
    this.locationGrid.forEach(row => {
      row.forEach((_, j, array) => {
        array[j] = false;
      });
    });
  }

  /**
   * A two-dimensional array is created that contains all occupied positions on the grid.
   * This array helps for collision checks and can deliver basic information about cards and their neighbouring cells.
   * The array is a significant performance improvement compared to searching for positions in the data structure.
   */
  public initLocationGrid() {
    this.locationGrid = new Array(this.gridSize).fill(null).map(() => new Array(this.gridSize).fill(false));
  }

  public addCardToLocationGrid(gridX: number, gridY: number) {
    this.locationGrid[gridX][gridY] = true;
  }

  public removeCardFromLocationGrid(gridX: number, gridY: number) {
    this.locationGrid[gridX][gridY] = false;

  }

  public checkCardCollision(coordX: number, coordY: number) {
    return (this.checkCardOutOfBounds(coordX, coordY) ||
      this.locationGrid[this.calculateGridPositionX(coordX)][this.calculateGridPositionY(coordY, coordX)]);
  }

  public checkCardOutOfBounds(coordX: number, coordY: number): boolean {
    return (coordX < 0
      || coordY < 0
      || this.calculateGridPositionY(coordY, coordX) > this.gridSize
      || this.calculateGridPositionX(coordX) >= this.gridSize);
  }

  public updateAllLinkageCards() {
    console.log('updateAllLinkageCards');
    const cardsToRearrangeLinkageCards = this.removeAllLinkageCardsForRepositioning();
    let rearrangedRelations: GameDesignRelation[] = [];
    if (cardsToRearrangeLinkageCards !== undefined) {
      for (const card of cardsToRearrangeLinkageCards) {
        const sourceCard = this.getCardWebElement(card.id);
        console.log('rearrange', sourceCard.name);
        rearrangedRelations = this.rearrangeConnections(sourceCard.id, sourceCard.name, rearrangedRelations);
      }
    }
  }

  public changeCardLocationInLocationGrid(
    movedCardId: number, initialPosX: number, initialPosY: number, newXGrid: number, newYGrid: number, sendWebSocketMessage = true) {
    const cardsToRearrangeLinkageCards = this.removeAllLinkageCardsForRepositioning();
    this.addCardToLocationGrid(newXGrid, newYGrid);
    this.removeCardFromLocationGrid(initialPosX, initialPosY);
    // Ensure that the minimal amount of reArrangements and shortest path calculations are done
    let rearrangedRelations: GameDesignRelation[] = [];
    let movedCard = undefined;
    if (this.hasCardWebElement(movedCardId)) {
      const cardIndex = this.cardWeb.cards.findIndex(card => card.id === movedCardId);
      if (cardIndex !== -1) {
        this.cardWeb.cards[cardIndex].posX = newXGrid;
        this.cardWeb.cards[cardIndex].posY = newYGrid;

        // Remove all LinkageCards related to the card that was moved
        // This set saves all ids from cards that have linkageCards affected by this repositioning
        movedCard = this.getCardWebElement(movedCardId);

        rearrangedRelations = this.rearrangeConnections(movedCard.id, movedCard.name);
      }
    }

    if (cardsToRearrangeLinkageCards !== undefined) {
      for (const card of cardsToRearrangeLinkageCards) {
        const sourceCard = this.getCardWebElement(card.id);
        if (movedCardId !== sourceCard.id) {
          rearrangedRelations = this.rearrangeConnections(sourceCard.id, sourceCard.name, rearrangedRelations);
        }
      }
    }
    if (movedCard !== undefined) {
      this.cw$.next(this.getCustomCardWeb());

      if (sendWebSocketMessage) {
        // CALL WEBSOCKET CONNECTION
        const cardObject = this.mfs.buildCardObject(movedCard.cardDeckId, movedCard.id, movedCard.deckType, movedCard.name);
        const moveEventData = this.mfs.buildMoveEventData(newXGrid, newYGrid);
        const messageObject = this.mfs.createMessageMoveCard(cardObject, moveEventData);
        this.ccs.handleAction(messageObject);
      }
    }
  }

  getNeighboursForPosition(posX, posY, partOfPosition: 'up'|'down') {
    const neighboursMap = new Map<(LinkageDirection), CardWebElement>();
    let neighbourCard = this.getNeighbourCardForPosition(posX, posY, partOfPosition, LinkageDirection.left);
    if (neighbourCard) {
      neighboursMap.set(LinkageDirection.left, neighbourCard);
    }
    neighbourCard = this.getNeighbourCardForPosition(posX, posY, partOfPosition, LinkageDirection.middle);
    if (neighbourCard) {
      neighboursMap.set(LinkageDirection.middle, neighbourCard);
    }
    neighbourCard = this.getNeighbourCardForPosition(posX, posY, partOfPosition, LinkageDirection.right);
    if (neighbourCard) {
      neighboursMap.set(LinkageDirection.right, neighbourCard);
    }
    return neighboursMap;
  }

  getNeighbourCardsForLinkageCard(partOfCard: 'up'|'down', linkageCard: CardWebElement) {
    const neighbourCards: CardWebElement[] = [];
    switch (partOfCard) {
      case 'up': {
        if (linkageCard.linkageUpLeftDirection) {
          const neighbourCard = this.getNeighbourCard(linkageCard.id, partOfCard, LinkageDirection.left);
          if (neighbourCard) {
            neighbourCards.push(neighbourCard);
          }
        }
        if (linkageCard.linkageUpRightDirection) {
          const neighbourCard = this.getNeighbourCard(linkageCard.id, partOfCard, LinkageDirection.right);
          if (neighbourCard) {
            neighbourCards.push(neighbourCard);
          }
        }
        if (linkageCard.linkageUpMiddleDirection) {
          const neighbourCard = this.getNeighbourCard(linkageCard.id, partOfCard, LinkageDirection.middle);
          if (neighbourCard) {
            neighbourCards.push(neighbourCard);
          }
        }
        break;
      }
      case 'down': {
        if (linkageCard.linkageDownLeftDirection) {
          const neighbourCard = this.getNeighbourCard(linkageCard.id, partOfCard, LinkageDirection.left);
          if (neighbourCard) {
            neighbourCards.push(neighbourCard);
          }
        }
        if (linkageCard.linkageDownRightDirection) {
          const neighbourCard = this.getNeighbourCard(linkageCard.id, partOfCard, LinkageDirection.right);
          if (neighbourCard) {
            neighbourCards.push(neighbourCard);
          }
        }
        if (linkageCard.linkageDownMiddleDirection) {
          const neighbourCard = this.getNeighbourCard(linkageCard.id, partOfCard, LinkageDirection.middle);
          if (neighbourCard) {
            neighbourCards.push(neighbourCard);
          }
        }
        break;
      }
      default: {}
    }
    return neighbourCards;
  }

  public loadCustomCardWeb(id: number) {
    if (this.cardWeb) {
      return;
    }
    this.cardWeb = {
      id: 1,
      name: 'CustomDeckOne',
      createdDateTime: new Date().toDateString(),
      ownerUserId: null,
      cards: []
    };
    this.cardWeb.cards.forEach(card => {
      this.locationGrid[card.posX][card.posY] = true;
    });
    this.cw$ = new BehaviorSubject<CardWeb>(this.cardWeb);
  }

  /**
   * Overwrite CardWeb after server sends saved object
   */
  loadCardWebByServer(cardWeb: CardWeb) {
    console.log('::::::::::::::::::::LOAD CARDWEB ' + cardWeb.name);
    if (this.cardWeb) {
      this.cardWeb = cardWeb;
      this.mfs.initialize(this.getCustomCardWeb().name, this.getCustomCardWeb().createdDateTime);
      this.cw$.next(this.cardWeb);
      // Init LocationGrid to prevent Errors with overlapping locationGrids.
      this.initLocationGrid();
      this.cardWeb.cards.forEach(cardWebElement => {
        this.addCardToLocationGrid(cardWebElement.posX, cardWebElement.posY);
      });
      return;
    }
    this.cardWeb = cardWeb;
    this.mfs.initialize(this.getCustomCardWeb().name, this.getCustomCardWeb().createdDateTime);
    this.cw$ = new BehaviorSubject<CardWeb>(this.cardWeb);
    this.cardWeb.cards.forEach(cardWebElement => {
      this.addCardToLocationGrid(cardWebElement.posX, cardWebElement.posY);
    });
  }

  public calculateGridPositionX(coordinateX: number) {
    return coordinateX / 400;
  }

  public calculateGridPositionY(coordinateY: number, coordinateX: number) {
    if ((coordinateX) % 800 === 0) {
      return (coordinateY / 500) * 2;
    } else {
      // console.log('x: ' + coordinateX + 'y: ' + coordinateY + ' res: ' + (((coordinateY + 250) / 500) * 2 - 1));
      return (((coordinateY + 250) / 500) * 2 - 1);
    }
  }

  public hasCardWebElement(id: number): boolean {
    for (const card of this.cardWeb.cards) {
      if (card.id === id) {
        return true;
      }
    }
  }

  private addLinkageCardWebElement(newLinkageCard: CardWebElement) {
    if (!this.hasCardWebElement(newLinkageCard.id)) {
      this.addCardToLocationGrid(newLinkageCard.posX, newLinkageCard.posY);
      this.cardWeb.cards.push(newLinkageCard);
    } else {
      // Modify existing LinkageCardWebElement
      const existingLinkageCard = this.getCardWebElement(newLinkageCard.id);
      if (existingLinkageCard.cardDeckType === DeckType.linkage) {
        this.removeLinkageCardElement(existingLinkageCard);
        newLinkageCard = this.ls.mergeLinkageCards(existingLinkageCard, newLinkageCard);
        this.addCardToLocationGrid(newLinkageCard.posX, newLinkageCard.posY);
        this.cardWeb.cards.push(newLinkageCard);
        this.cw$.next(this.getCustomCardWeb());
      }
    }
  }

  public getPathTypeForLinkageCard(linkageCard: CardWebElement): PathType {
    return this.ls.getPathTypeForLinkageCard(linkageCard);
  }

  public addCardWebElement(
    id: number,
    name: string,
    cardDeckId: number,
    cardDeckType: DeckType,
    posX: number,
    posY: number,
    sendWebSocketMessage = true) {
    if (!this.hasCardWebElement(id) && this.hasCustomCardWeb()) {
      const cardWebElement: CardWebElement = {
        id,
        name,
        cardDeckId,
        cardDeckType,
        posX,
        posY
        // posX: this.calculateGridPositionX(posX),
        // posY: this.calculateGridPositionY(posY, posX)
      };
      this.cardWeb.cards.push(cardWebElement);
      this.gs.addGameDesignElement(id, true, cardWebElement.name, cardDeckType);
      this.addCardToLocationGrid(cardWebElement.posX, cardWebElement.posY);
    }
    this.cw$.next(this.getCustomCardWeb());

    if (sendWebSocketMessage) {
      // CALL WEBSOCKET CONNECTION
      const cardObject = this.mfs.buildCardObject(cardDeckId, id, cardDeckType, name);
      const moveEventData = this.mfs.buildMoveEventData(posX, posY);
      const messageObject = this.mfs.createMessageAddCard(cardObject, moveEventData);
      this.ccs.handleAction(messageObject);
    }
  }

  getOverallLinkageType(linkageCard: CardWebElement) {
    const allLinkageCards = this.getAllLinkageCards();
    return this.ls.getOverallLinkageType(linkageCard, allLinkageCards);
  }

  public removeAllLinkageCardsForRepositioning() {
    const sourceCardToRearrangeForLinkageCards = new Set<CardWebElement>();
    for (let i = 0; i < this.cardWeb.cards.length; i++) {
      const card = this.cardWeb.cards[i];
      if (card.cardDeckType === DeckType.linkage) {
        for (const sourceId of card.linkageSourceIds) {
          const sourceCard = this.getCardWebElement(sourceId);
          sourceCardToRearrangeForLinkageCards.add(sourceCard);
        }
        /*for (const targetId of card.linkageTargetIds) {
          const targetCard = this.getCardWebElement(targetId);
          sourceCardToRearrangeForLinkageCards.add(targetCard);
        }*/
        this.removeCardFromLocationGrid(card.posX, card.posY);
        this.cardWeb.cards.splice(i--, 1);
      }
    }
    // const sortedArray = Array.from(sourceCardToRearrangeForLinkageCards).sort((c1, c2) => c2.posX - c1.posX);
    // const sortedArray = Array.from(sourceCardToRearrangeForLinkageCards).sort((c1, c2) => c1.posX - c2.posX);
    return sourceCardToRearrangeForLinkageCards;
  }

  public removeLinkageCardElement(linkageCardToDelete: CardWebElement) {
    this.cardWeb.cards.forEach((card, index) => {
      if (card.id === linkageCardToDelete.id) {
        this.cardWeb.cards.splice(index, 1);
        this.removeCardFromLocationGrid(linkageCardToDelete.posX, linkageCardToDelete.posY);
      }
    });
  }

  private getAllLinkageCards() {
    return this.cardWeb.cards.filter(card => card.cardDeckType === DeckType.linkage);
  }

  removeAllLinkageCardsForCardId(
      originalSourceId: number,
      originalTargetId: number,
      removeGameDesignRelationAsWell = true)
  {
    const allLinkageCards = this.getAllLinkageCards();
    const linkageCardsToDeleteTarget = this.ls.getAllLinkageCardsForTargetId(originalTargetId, allLinkageCards);
    const linkageCardsToDeleteSource = this.ls.getAllLinkageCardsForSourceId(originalSourceId, allLinkageCards);
    // Union of Arrays
    const linkageCardsToDelete: CardWebElement[] = [];
    linkageCardsToDelete.push(...linkageCardsToDeleteSource);
    linkageCardsToDelete.push(...linkageCardsToDeleteTarget);
    for (const linkageCardToDelete of linkageCardsToDelete) {
      this.removeLinkageCardElement(linkageCardToDelete);
    }
    const sourceCard = this.cardWeb.cards.find(card => card.id === originalSourceId);
    if (removeGameDesignRelationAsWell) {
        this.gs.removeGameDesignRelation(originalSourceId, originalTargetId, sourceCard.cardDeckType);
      }
    this.cw$.next(this.getCustomCardWeb());
  }

  removeLinkageCard(cardToDelete: CardWebElement, removeGameDesignRelationAsWell: boolean = true){
    let snackBarText = '';
    if (cardToDelete.linkageType === LinkageType.oneToOne) {
      const sourceCard = this.getCardWebElement(cardToDelete.linkageSourceIds[0]);
      const targetCard = this.getCardWebElement(cardToDelete.linkageTargetIds[0]);
      this.removeAllLinkageCardsForCardId(sourceCard.id, targetCard.id, removeGameDesignRelationAsWell);
      snackBarText += 'Verbindung von ' + sourceCard.name + ' zu ' + targetCard.name + ' entfernt. ';
      this.rearrangeConnections(sourceCard.id, sourceCard.name);
      this.rearrangeConnections(targetCard.id, targetCard.name);
    }else if (cardToDelete.linkageType === LinkageType.manyToOne) {
      for (const sourceId of cardToDelete.linkageSourceIds) {
        const sourceCard = this.getCardWebElement(sourceId);
        const targetCard = this.getCardWebElement(cardToDelete.linkageTargetIds[0]);
        this.removeAllLinkageCardsForCardId(sourceCard.id, targetCard.id, removeGameDesignRelationAsWell);
        snackBarText += 'Verbindung von ' + sourceCard.name + ' zu ' + targetCard.name + ' entfernt. ';
        this.rearrangeConnections(targetCard.id, targetCard.name);
      }
    }else if (cardToDelete.linkageType === LinkageType.oneToMany) {
      for (const targetId of cardToDelete.linkageTargetIds) {
        const sourceCard = this.getCardWebElement(cardToDelete.linkageSourceIds[0]);
        const targetCard = this.getCardWebElement(targetId);
        this.removeAllLinkageCardsForCardId(sourceCard.id, targetCard.id, removeGameDesignRelationAsWell);
        snackBarText += 'Verbindung von ' + sourceCard.name + ' zu ' + targetCard.name + ' entfernt. ';
        this.rearrangeConnections(sourceCard.id, sourceCard.name);
      }
    }
    if (snackBarText.length > 0) {
      this.snackBar.open(snackBarText, '',
          {
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            duration: 3000
          });
    }
  }

  removeCardWebElement(cardToDelete: CardWebElement, sendWebSocketMessage = true) {
    const cardsToRearrangeLinkageCards = this.removeAllLinkageCardsForRepositioning();

    this.cardWeb.cards.forEach((card, index) => {
      if (card.id === cardToDelete.id) {
        this.cardWeb.cards.splice(index, 1);
        this.removeCardFromLocationGrid(card.posX, card.posY);
        this.gs.removeGameDesignElement(cardToDelete.id);
        this.snackBar.open(card.name + ' und dazugehörige Verbindungen entfernt', '',
          {
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            duration: 3000
          });
      }
    });

    if (cardsToRearrangeLinkageCards !== undefined) {
      for (const card of cardsToRearrangeLinkageCards) {
        if (this.hasCardWebElement(card.id)) {
          const sourceCard = this.getCardWebElement(card.id);
          this.rearrangeConnections(sourceCard.id, sourceCard.name);
        }
      }
    }

    this.cw$.next(this.getCustomCardWeb());

    if (sendWebSocketMessage) {
      // CALL WEBSOCKET CONNECTION
      const cardObject = this.mfs.buildCardObject(cardToDelete.cardDeckId, cardToDelete.id, cardToDelete.cardDeckType, cardToDelete.name);
      const messageObject = this.mfs.createMessageDeleteCard(cardObject);
      this.ccs.handleAction(messageObject);
    }
  }

  public getCustomCardWebBehaviorSubject(): BehaviorSubject<CardWeb> {
    this.checkIfCustomCardWebIsInitialized();
    if (this.cw$ === undefined) {
      this.cw$ = new BehaviorSubject<CardWeb>(this.getCustomCardWeb());
    }
    return this.cw$;
  }

  public addDroppedCard(
    id: number,
    name: string,
    cardDeckId: number,
    cardDeckType: DeckType,
    posX: number,
    posY: number) {
    const possiblePosX = Math.round((posX - 150) / 400) * 400;
    let possiblePosY = (Math.round((posY - 150) / 500) * 500);
    if (possiblePosX % 800 !== 0) {
      possiblePosY = possiblePosY - 250;
    }
    const possibleGridX = this.calculateGridPositionX(possiblePosX);
    const possibleGridY = this.calculateGridPositionY(possiblePosY, possiblePosX);

    if (this.locationGrid[possibleGridX][possibleGridY] === true) {
      const nearestFreeCell = this.calculateNearestFreeCell(this.locationGrid, possibleGridX, possibleGridY);
      this.addCardWebElement(id, name, cardDeckId, cardDeckType, nearestFreeCell.row, nearestFreeCell.column);
    } else {
      this.addCardWebElement(id, name, cardDeckId, cardDeckType, possibleGridX, possibleGridY);
    }
  }

  calculateNearestFreeCell(posMatrix: boolean[][], gridX: number, gridY: number): { row: number, column: number } | null {
    const rows = posMatrix.length;
    const cols = posMatrix[0].length;

    const queue: [number, number][] = [];
    const visited: boolean[][] = Array.from({length: rows}, () => Array(cols).fill(false));

    // Define directions for neighboring cells
    const directions: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];

    // Add the starting position to the queue
    queue.push([gridX, gridY]);
    visited[gridX][gridY] = true;

    while (queue.length > 0) {
      const nextPos = queue.shift();
      if (!nextPos) {
        continue;
      }

      const [r, c] = nextPos;

      // Check if the current position is free
      if (!posMatrix[r][c] && this.checkForValidCell(r, c)) {
        return {row: r, column: c};
      }

      // Explore the neighboring cells
      for (const [dr, dc] of directions) {
        const newRow = r + dr;
        const newCol = c + dc;

        // Check if the neighboring cell is within bounds and not visited
        if (newRow >= 0 && newRow < rows && newCol >= 0 && newCol < cols && !visited[newRow][newCol]) {
          queue.push([newRow, newCol]);
          visited[newRow][newCol] = true;
        }
      }
    }

    return null;
  }

  checkForValidCell(gridX: number, gridY: number): boolean {
    if (((gridX % 2 === 0) && (gridY % 2 === 0)) || ((gridX % 2 !== 0) && (gridY % 2 !== 0))) {
      return true;
    } else {
      return false;
    }
  }

  private isConnectionAllowed(linkageDirection: LinkageDirection, sourceId: number, posX: number, posY: number) {
    const sourceCard = this.cardWeb.cards.find(card => card.id === sourceId);
    const targetCard = this.getNeighbourCard(sourceId, 'down', linkageDirection);
    let result = false;
    if (targetCard === null) {
      return true;
    }
    if (sourceCard.cardDeckType === DeckType.pattern) {
      if (targetCard.cardDeckType === DeckType.pattern) {
        result = true;
      }else if (targetCard.cardDeckType === DeckType.linkage) {
        const overallLinkageTypeTarget = this.getOverallLinkageType(targetCard);
        if (overallLinkageTypeTarget === LinkageType.oneToOne || overallLinkageTypeTarget === LinkageType.manyToOne) {
          // Check if original targetCard is pattern
          const originalTargetCard = this.getCardWebElement(targetCard.linkageTargetIds[0]);
          if (originalTargetCard.cardDeckType === DeckType.pattern) {
            result = true;
          }
        }
      }
    }else if (sourceCard.cardDeckType === DeckType.problem) {
      if (targetCard.cardDeckType !== DeckType.linkage) {
        result = true;
      }else if (targetCard.cardDeckType === DeckType.linkage) {
        const overallLinkageTypeTarget = this.getOverallLinkageType(targetCard);
        if (overallLinkageTypeTarget === LinkageType.oneToOne || overallLinkageTypeTarget === LinkageType.manyToOne) {
          result = true;
        }
      }
    }else if (sourceCard.cardDeckType === DeckType.linkage) {
      const overallLinkageTypeSource = this.getOverallLinkageType(sourceCard);
      if (overallLinkageTypeSource === LinkageType.oneToOne || overallLinkageTypeSource === LinkageType.oneToMany) {
        if (targetCard.cardDeckType === DeckType.pattern) {
          result = true;
        }else if (targetCard.cardDeckType === DeckType.problem) {
          // Check if original source card is problem
          const originalSourceCard = this.getCardWebElement(sourceCard.linkageSourceIds[0]);
          if (originalSourceCard.cardDeckType === DeckType.problem) {
            result = true;
          }
        }
      }
    }

    return result;
  }

  public canConnectLeft(posX: number, posY: number, sourceId: number) {
    if ((posX - 1 < 0) || (posY + 1 > this.gridSize)) {
      return false;
    } else {
      const hasConnection = !this.hasConnection(LinkageDirection.left, sourceId, posX, posY);
      if (hasConnection) {
        return this.isConnectionAllowed(LinkageDirection.left, sourceId, posX, posY);
      }else{
        return false;
      }
    }
  }

  public canConnectRight(posX: number, posY: number, sourceId: number) {
    if ((posX >= this.gridSize - 1) || (posY + 1 > this.gridSize)) {
      return false;
    } else {
        const hasConnection = !this.hasConnection(LinkageDirection.right, sourceId, posX, posY);
        if (hasConnection) {
            return this.isConnectionAllowed(LinkageDirection.right, sourceId, posX, posY);
        }else{
            return false;
        }
    }
  }

  public canConnectBottom(posX: number, posY: number, sourceId: number) {
    if ((posY + 2 > this.gridSize)) {
      return false;
    } else {
        const hasConnection = !this.hasConnection(LinkageDirection.middle, sourceId, posX, posY);
        if (hasConnection) {
            return this.isConnectionAllowed(LinkageDirection.middle, sourceId, posX, posY);
        }else{
            return false;
        }
    }
  }

  public canHaveNote(cardId: number) {
    if (this.hasCardWebElement(cardId)) {
      return this.getCardWebElement(cardId).cardDeckType !== DeckType.linkage;
    }
    return false;
  }

  private checkForTransitiveRelationToTargetCard(sourceId: number, neighbouringCard: CardWebElement) {
    let result = false;
    if (neighbouringCard.cardDeckType === DeckType.linkage) {
      if (neighbouringCard.linkageSourceIds.includes(sourceId)) {
        result = true;
      }else{
        const overallLinkageType = this.getOverallLinkageType(neighbouringCard);
        if (overallLinkageType === LinkageType.manyToOne || overallLinkageType === LinkageType.oneToOne) {
          const originalTargetCard = this.getCardWebElement(neighbouringCard.linkageTargetIds[0]);
          result = this.gs.hasGameDesignRelation(sourceId, originalTargetCard.id)
              || this.gs.hasGameDesignProblemRelation(sourceId, originalTargetCard.id);
        }
      }
    }else {
      const sourceCard = this.getCardWebElement(sourceId);
      // Null Check needed to prevent errors from delete webSocket Messages
      if (sourceCard !== null) {
        let sourceCardId = sourceId;
        if (sourceCard.cardDeckType === DeckType.linkage) {
          if (sourceCard.linkageType === LinkageType.oneToOne || sourceCard.linkageType === LinkageType.oneToMany) {
            sourceCardId = sourceCard.linkageSourceIds[0];
          }
        }
        result = this.gs.hasGameDesignRelation(sourceCardId, neighbouringCard.id)
          || this.gs.hasGameDesignProblemRelation(sourceCardId, neighbouringCard.id);
      }
    }
    return result;
  }

  public hasConnection(linkageDirection: LinkageDirection, sourceId: number, posX: number, posY: number) {
    let hasConnection = true;
    switch (linkageDirection) {
      case LinkageDirection.left: {
        if (this.locationGrid[posX - 1][posY + 1]) {
          const neighbouringCard = this.cardWeb.cards.find(card => card.posX === posX - 1 && card.posY === posY + 1);
          hasConnection = this.checkForTransitiveRelationToTargetCard(sourceId, neighbouringCard);
        } else {
          hasConnection = false;
        }
        break;
      }
      case LinkageDirection.right: {
        if (this.locationGrid[posX + 1][posY + 1]) {
          const neighbouringCard = this.cardWeb.cards.find(card => card.posX === posX + 1 && card.posY === posY + 1);
          hasConnection = this.checkForTransitiveRelationToTargetCard(sourceId, neighbouringCard);
        } else {
          hasConnection = false;
        }
        break;
      }
      case LinkageDirection.middle: {
        if (this.locationGrid[posX][posY + 2]) {
          const neighbouringCard = this.cardWeb.cards.find(card => card.posX === posX && card.posY === posY + 2);
          hasConnection = this.checkForTransitiveRelationToTargetCard(sourceId, neighbouringCard);
        } else {
          hasConnection = false;
        }
        break;
      }
      default: {}
    }
    return hasConnection;
  }

  public hasFreeConnector(sourceId: number, targetId: number) {
    let result = false;
    const hasDirectFreeConnector = this.hasDirectFreeConnector(sourceId, targetId);
    if (hasDirectFreeConnector) {
      result = true;
    }else {
      // No direct free connector available. Check if there is space via linkage cards
      const linkageCardsWithSourceId = this.cardWeb.cards.filter(card =>
        card.cardDeckType === DeckType.linkage && card.linkageSourceIds.includes(sourceId));
      for (const linkageCardWithSourceId of linkageCardsWithSourceId) {
        if (this.hasDirectFreeConnector(linkageCardWithSourceId.id, targetId)) {
          result = true;
        }
      }
    }
    return result;
  }

  private hasDirectFreeConnector(sourceId: number, targetId: number) {
    const source = this.cardWeb.cards.find(card => card.id === sourceId);
    let result = false;
    if (this.locationGrid[source.posX - 1][source.posY + 1]) {
      result = this.cardWeb.cards.find(card => card.posX === source.posX - 1 && card.posY === source.posY + 1).id === targetId;
    } else {
      result = true;
    }
    if (!result) {
      if (this.locationGrid[source.posX + 1][source.posY + 1]) {
          result = this.cardWeb.cards.find(card => card.posX === source.posX + 1 && card.posY === source.posY + 1).id === targetId;
      } else {
        result = true;
      }
    }
    if (!result) {
      if (this.locationGrid[source.posX][source.posY + 2]) {
        result = this.cardWeb.cards.find(card => card.posX === source.posX && card.posY === source.posY + 2).id === targetId;
      } else {
        result = true;
      }
    }
    return result;
  }

  public getNewTargetCoord(sourceId: number, targetId: number) {
    const source = this.cardWeb.cards.find(card => card.id === sourceId);
    let x: number;
    let y: number;
    if (!this.connectionPreference) {
      this.connectionPreference = 'left';
    }
    let cardPosX = source.posX;
    let cardPosY = source.posY;
    if (this.connectionPreferencePosX !== null) {
      cardPosX = this.connectionPreferencePosX;
    }
    if (this.connectionPreferencePosY !== null) {
      cardPosY = this.connectionPreferencePosY;
    }
    switch (this.connectionPreference) {
      case ('left'):
        if (!this.locationGrid[cardPosX - 1][cardPosY + 1]) {
          x = cardPosX - 1;
          y = cardPosY + 1;
          return {x, y};
        } else if (!this.locationGrid[cardPosX + 1][cardPosY + 1]) {
          x = cardPosX + 1;
          y = cardPosY + 1;
          return {x, y};
        } else if (!this.locationGrid[cardPosX][cardPosY + 2]) {
          x = cardPosX;
          y = cardPosY + 2;
          return {x, y};
        }
        break;
      case ('right'):
        if (!this.locationGrid[cardPosX + 1][cardPosY + 1]) {
          x = cardPosX + 1;
          y = cardPosY + 1;
          return {x, y};
        } else if (!this.locationGrid[cardPosX - 1][cardPosY + 1]) {
          x = cardPosX - 1;
          y = cardPosY + 1;
          return {x, y};
        } else if (!this.locationGrid[cardPosX][cardPosY + 2]) {
          x = cardPosX;
          y = cardPosY + 2;
          return {x, y};
        }
        break;
      case ('bottom'):
        if (!this.locationGrid[cardPosX][cardPosY + 2]) {
          x = cardPosX;
          y = cardPosY + 2;
          return {x, y};
        } else if (!this.locationGrid[cardPosX - 1][cardPosY + 1]) {
          x = cardPosX - 1;
          y = cardPosY + 1;
          return {x, y};
        } else if (!this.locationGrid[cardPosX + 1][cardPosY + 1]) {
          x = cardPosX + 1;
          y = cardPosY + 1;
          return {x, y};
        }

    }
  }

  public sendRemoveConnectionMessage(
    sourceId: number,
    targetId: number,
    sourceDeckType: DeckType,
    targetDeckType: DeckType,
    sourceName: string,
    targetName: string,
    relationId: number) {

    // CALL WEBSOCKET CONNECTION
    const linkObject = this.mfs.buildLinkObject(sourceId, targetId, sourceDeckType, targetDeckType, sourceName, targetName, relationId);
    const messageObject = this.mfs.createMessageDeleteLink(linkObject);
    this.ccs.handleAction(messageObject);
  }

  public sendAddConnectionMessage(
    sourceId: number,
    sourceName: string,
    sourceDeckType: DeckType,
    targetId: number,
    targetName: string,
    targetDeckType: DeckType,
    relationId: number)
  {

    // CALL WEBSOCKET CONNECTION
    const linkObject = this.mfs.buildLinkObject(sourceId, targetId, sourceDeckType, targetDeckType, sourceName, targetName, relationId);
    const messageObject = this.mfs.createMessageAddLink(linkObject);
    this.ccs.handleAction(messageObject);
  }

  public rearrangeConnections(cardId: number, cardName: string, rearrangedRelations: GameDesignRelation[] = []) {
    const gameDesign = this.gs.getGameDesign();
    for (let i = gameDesign.gameDesignRelations.length - 1; i > -1; i--) {
      const relation = gameDesign.gameDesignRelations[i];
      if (relation.sourceId === cardId || relation.targetId === cardId) {
        // Ensure that the minimal amount of reArrangements and shortest path calculations are done
        if (!rearrangedRelations.find(r => r.sourceId === relation.sourceId && r.targetId === relation.targetId)) {
          rearrangedRelations.push(relation);
          if (!this.createShortestPath(relation.sourceId, relation.targetId, cardId)) {
            this.snackBar.open('Verbindungen von ' + relation.sourceName + ' zu ' + relation.targetName
                + ' entfernt', '',
                {
                  horizontalPosition: 'right',
                  verticalPosition: 'bottom',
                  duration: 3000
                });
            this.gs.removeGameDesignRelationItem(
                gameDesign.gameDesignRelations[i].sourceId,
                gameDesign.gameDesignRelations[i].targetId,
                gameDesign.gameDesignRelations[i].relationItemId,
                DeckType.pattern, false);
          }
        }
      }
    }
    for (let i = gameDesign.gameDesignProblemRelations.length - 1; i > -1; i--) {
      const relation = gameDesign.gameDesignProblemRelations[i];
      if (relation.sourceId === cardId || relation.targetId === cardId) {
        // Ensure that the minimal amount of reArrangements and shortest path calculations are done
        if (!rearrangedRelations.find(r => r.sourceId === relation.sourceId && r.targetId === relation.targetId)) {
          rearrangedRelations.push(relation);
          if (!this.createShortestPath(relation.sourceId, relation.targetId, cardId)) {
            this.snackBar.open('Verbindungen von ' + relation.sourceName + ' zu ' + relation.targetName
                + ' entfernt', '',
                {
                  horizontalPosition: 'right',
                  verticalPosition: 'bottom',
                  duration: 3000
                });
            this.gs.removeGameDesignRelationItem(
                gameDesign.gameDesignProblemRelations[i].sourceId,
                gameDesign.gameDesignProblemRelations[i].targetId,
                gameDesign.gameDesignProblemRelations[i].relationItemId,
                DeckType.problem, false);
          }
        }
      }
    }
    return rearrangedRelations;
  }

  public hasShortestPath(sourceId: number, targetId: number) {
    const sourceCard = this.getCardWebElement(sourceId);
    const targetCard = this.getCardWebElement(targetId);
    const shortestPath = this.ls.searchShortestPath(sourceCard, targetCard, this.locationGrid, this.cardWeb, sourceCard);
    return shortestPath.length > 0;
  }

  public createShortestPath(sourceId: number, targetId: number, draggedCardId: number = sourceId) {
    const sourceCard = this.cardWeb.cards.find(card => card.id === sourceId);
    const targetCard = this.cardWeb.cards.find(card => card.id === targetId);
    const draggedCard = this.cardWeb.cards.find(card => card.id === draggedCardId);
    const shortestPath = this.ls.searchShortestPath(sourceCard, targetCard, this.locationGrid, this.cardWeb, draggedCard);
    const linkageCards = this.ls.getLinkageCardWebElementsForShortestPath(shortestPath, sourceCard, targetCard);
    if (shortestPath.length === 2) {
      // No Additional LinkageCards needed
      return true;
    }else if (shortestPath.length > 2) {
      // Additional LinkageCards needed
      for (const linkageCardWebElement of linkageCards) {
        this.addLinkageCardWebElement(linkageCardWebElement);
      }
      return true;
    }else if (shortestPath.length === 0) {
      // Delete connection as no shortestPath was found
      return false;
    }
  }



  public setConnectionPreference(preference: ('left' | 'right' | 'bottom'), sourceXPos: number = null, sourceYPos: number = null) {
    this.connectionPreference = preference;
    this.connectionPreferencePosX = sourceXPos;
    this.connectionPreferencePosY = sourceYPos;
  }

  public getNeighbourCardForPosition(posX: number, posY: number, partOfCard: 'up' | 'down', linkageDirection: LinkageDirection) {
    let neighbourCard: CardWebElement;
    switch (partOfCard) {
      case 'up': {
        if (linkageDirection === LinkageDirection.left) {
          neighbourCard = this.cardWeb.cards.find(card => card.posX === posX - 1 && card.posY === posY - 1);
        }else if (linkageDirection === LinkageDirection.right) {
          neighbourCard = this.cardWeb.cards.find(card => card.posX === posX + 1 && card.posY === posY - 1);
        }else if (linkageDirection === LinkageDirection.middle) {
          neighbourCard = this.cardWeb.cards.find(card => card.posX === posX && card.posY === posY - 2);
        }
        break;
      }
      case 'down': {
        if (linkageDirection === LinkageDirection.left) {
          neighbourCard = this.cardWeb.cards.find(card => card.posX === posX - 1 && card.posY === posY + 1);
        }else if (linkageDirection === LinkageDirection.right) {
          neighbourCard = this.cardWeb.cards.find(card => card.posX === posX + 1 && card.posY === posY + 1);
        }else if (linkageDirection === LinkageDirection.middle) {
          neighbourCard = this.cardWeb.cards.find(card => card.posX === posX && card.posY === posY + 2);
        }
        break;
      }
      default: {}
    }
    if (neighbourCard !== undefined) {
      return neighbourCard;
    }else{
      return null;
    }
  }

  public getNeighbourCard(cardId: number, partOfCard: ('up' | 'down'), linkageDirection: LinkageDirection){
    let posX;
    let posY;
    const card = this.cardWeb.cards.find(card => card.id === cardId);
    if (card !== undefined) {
      posX = card.posX;
      posY = card.posY;
      return this.getNeighbourCardForPosition(posX, posY, partOfCard, linkageDirection);
    }else{
      return null;
    }
  }

  /*
   * Returns all Cards which are in direct or indirect (via linkage cards) reach for a new connection
   */
  public getReachableNeighbourCardIds(id: number, direction: 'up'|'down', originalId = id) {
    const allNeighboursMap = new Set<number>();
    const directNeighbours = this.getNeighbourCardIds(id, direction);
    directNeighbours.forEach((neighbourId: number, key: 'left' | 'right' | 'bottom') => {
      const neighbourCard = this.getCardWebElement(neighbourId);
      if (neighbourCard.cardDeckType === DeckType.linkage) {
        const overallLinkageType = this.getOverallLinkageType(neighbourCard);
        if (direction === 'down') {
          if (overallLinkageType === LinkageType.oneToOne || overallLinkageType === LinkageType.manyToOne) {
            allNeighboursMap.add(neighbourCard.linkageTargetIds[0]);
            // Check if linkage card includes original card as source card then also check reachable neighbours
          }
          if (neighbourCard.linkageSourceIds.includes(originalId)) {
            const directNeighboursLevel2 = this.getReachableNeighbourCardIds(neighbourCard.id, direction, id);
            for (const neighbourCardId2 of directNeighboursLevel2) {
              const neighbourCard2 = this.getCardWebElement(neighbourCardId2);
              if (neighbourCard2.cardDeckType !== DeckType.linkage) {
                allNeighboursMap.add(neighbourCardId2);
              }
            }
          }
        }else if (direction === 'up') {
          if (overallLinkageType === LinkageType.oneToOne || overallLinkageType === LinkageType.oneToMany) {
            allNeighboursMap.add(neighbourCard.linkageSourceIds[0]);
            // Check if linkage card includes original card as source card then also check reachable neighbours
            if (neighbourCard.linkageSourceIds.includes(id)) {
              const directNeighboursLevel2 = this.getReachableNeighbourCardIds(neighbourCard.id, direction, id);
              for (const neighbourCardId2 of directNeighboursLevel2) {
                const neighbourCard2 = this.getCardWebElement(neighbourCardId2);
                if (neighbourCard2.cardDeckType !== DeckType.linkage) {
                  allNeighboursMap.add(neighbourCardId2);
                }
              }
            }
          }
        }
      }else{
        allNeighboursMap.add(neighbourId);
      }
    });
    return allNeighboursMap;
}

  public getNeighbourCardIds(id: number, direction: 'up'|'down') {
    const neighboursMap = new Map<('left' | 'right' | 'bottom'), number>();
    if (this.hasCardWebElement(id)) {
      try {
        let posX;
        let posY;
        let neighbourLeft: CardWebElement;
        let neighbourRight: CardWebElement;
        let neighbourMiddle: CardWebElement;
        const cardIndex = this.cardWeb.cards.findIndex(card => card.id === id);
        if (cardIndex !== -1) {
          posX = this.cardWeb.cards[cardIndex].posX;
          posY = this.cardWeb.cards[cardIndex].posY;
        }
        if (direction === 'up') {
          neighbourLeft = this.cardWeb.cards.find(card => card.posX === posX - 1 && card.posY === posY - 1);
          neighbourRight = this.cardWeb.cards.find(card => card.posX === posX + 1 && card.posY === posY - 1);
          neighbourMiddle = this.cardWeb.cards.find(card => card.posX === posX && card.posY === posY - 2);
        }else{
          neighbourLeft = this.cardWeb.cards.find(card => card.posX === posX - 1 && card.posY === posY + 1);
          neighbourRight = this.cardWeb.cards.find(card => card.posX === posX + 1 && card.posY === posY + 1);
          neighbourMiddle = this.cardWeb.cards.find(card => card.posX === posX && card.posY === posY + 2);
        }
        if (neighbourLeft !== undefined) {
          neighboursMap.set('left', neighbourLeft.id);
        }
        if (neighbourRight !== undefined) {
          neighboursMap.set('right', neighbourRight.id);
        }
        if (neighbourMiddle !== undefined) {
          neighboursMap.set('bottom', neighbourMiddle.id);
        }
      } catch (Error) {
        console.log(Error);
      }
    }
    return neighboursMap;
  }

  public getCardWebElement(id: number) {
    for (const card of this.cardWeb.cards) {
      if (card.id === id) {
        return card;
      }
    }
    console.log('No CardWebElement found for id ' + id);
    return null;
  }

  public getBoundingBox() {
    let topLeftX: number;
    let topLeftY: number;
    let bottomRightX: number;
    let bottomRightY: number;
    let minRow = this.gridSize;
    let minCol = this.gridSize;
    let maxRow = -1;
    let maxCol = -1;
    let foundTrue = false;

    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        if (this.locationGrid[row][col]) {
          if (!foundTrue) {
            foundTrue = true;
          } // Found at least one true value
          if (row < minRow) {
            minRow = row;
          }
          if (col < minCol) {
            minCol = col;
          }
          if (row > maxRow) {
            maxRow = row;
          }
          if (col > maxCol) {
            maxCol = col;
          }
        }
      }
    }

    if (foundTrue) {
      topLeftX = this.calculateCardXCoordinates(minRow) - 200;
      topLeftY = this.calculateCardYCoordinates(minCol, minRow) - 200;
      bottomRightX = this.calculateCardXCoordinates(maxRow) + 500;
      bottomRightY = this.calculateCardYCoordinates(maxCol, maxRow) + 500;

      const boundingBoxHeight = bottomRightY - topLeftY;
      const boundingBoxWidth = bottomRightX - topLeftX;

      return {x: topLeftX, y: topLeftY, height: boundingBoxHeight, width: boundingBoxWidth};
    } else {
      return {x: 0, y: 0, height: 100, width: 100};
    }
  }

  public calculateCardXCoordinates(gridPosX: number): number {
    return gridPosX * 400;
  }

  public calculateCardYCoordinates(gridPosY: number, gridPosX: number) {
    if ((gridPosX * 400) % 800 === 0) {
      return (gridPosY / 2) * 500;
    } else {
      return (((gridPosY - 1) / 2) * 500) + 250;
    }
  }

    getHoveredLinkageCard(coordinateX: number, coordinateY: number, draggedCardId: number) {
    const posX = this.calculateGridPositionX(coordinateX);
    const posY = this.calculateGridPositionY(coordinateY, coordinateX);
    if (this.locationGrid[posX][posY]) {
        const hoveredCard = this.cardWeb.cards.find(card => card.posX === posX && card.posY === posY);
        if (hoveredCard.cardDeckType === DeckType.linkage) {
          return hoveredCard;
        }
    }
    return undefined;
    }

  removeConnection(
      sourceCard: CardWebElement,
      targetCard: CardWebElement,
      originalSourceCard: CardWebElement,
      originalTargetCard: CardWebElement)
  {
    const relationsToDelete: {sourceCard: CardWebElement, targetCard: CardWebElement}[] = [];
    const cardsToRearrangeLinkageCards = this.removeAllLinkageCardsForRepositioning();
    if (sourceCard.cardDeckType === DeckType.linkage) {
      if (targetCard.cardDeckType !== DeckType.linkage) {
        relationsToDelete.push({
          sourceCard: originalSourceCard,
          targetCard: originalTargetCard
        });
      }
    }
    if (sourceCard.cardDeckType !== DeckType.linkage) {
      if (targetCard.cardDeckType === DeckType.linkage) {
        relationsToDelete.push({
          sourceCard: originalSourceCard,
          targetCard: originalTargetCard
        });
      }
    }
    if (sourceCard.cardDeckType === DeckType.linkage && targetCard.cardDeckType === DeckType.linkage) {
      if (sourceCard.linkageType === LinkageType.oneToOne || sourceCard.linkageType === LinkageType.manyToOne) {
        this.removeLinkageCardElement(sourceCard);
      }else{
        if (targetCard.linkageType === LinkageType.oneToOne || targetCard.linkageType === LinkageType.oneToMany) {
          this.removeLinkageCardElement(targetCard);
        }
      }
      for (const sourceCardId of sourceCard.linkageSourceIds) {
        for (const targetCardId of targetCard.linkageTargetIds) {
          relationsToDelete.push({
            sourceCard: this.getCardWebElement(sourceCardId),
            targetCard: this.getCardWebElement(targetCardId)
          });
        }
      }
    }

    if (sourceCard.cardDeckType !== DeckType.linkage && targetCard.cardDeckType !== DeckType.linkage) {
      relationsToDelete.push({
        sourceCard,
        targetCard
      });
    }

    for (const relationToDelete of relationsToDelete) {
      this.gs.removeGameDesignRelation(
          relationToDelete.sourceCard.id,
          relationToDelete.targetCard.id,
          relationToDelete.sourceCard.cardDeckType,
          false
      );
    }
    if (cardsToRearrangeLinkageCards !== undefined) {
      for (const card of cardsToRearrangeLinkageCards) {
        const sourceCard = this.getCardWebElement(card.id);
        this.rearrangeConnections(sourceCard.id, sourceCard.name);
      }
    }

    let snackBarText = '';

    for (const relationToDelete of relationsToDelete) {
      // connections might be removed automatically by changing cards
      // indicate to user that a connection has been removed
      snackBarText += 'Verbindungen von ' + relationToDelete.sourceCard.name + ' zu ' + relationToDelete.targetCard.name
          + ' entfernt. ';
      // CALL WEBSOCKET CONNECTION
      const linkObject = this.mfs.buildLinkObject(
        relationToDelete.sourceCard.id,
        relationToDelete.targetCard.id,
        relationToDelete.sourceCard.cardDeckType,
        relationToDelete.targetCard.cardDeckType,
        relationToDelete.sourceCard.name,
        relationToDelete.targetCard.name);
      const messageObject = this.mfs.createMessageDeleteLink(linkObject);
      this.ccs.handleAction(messageObject);
    }
    if (snackBarText.length > 0) {
      this.snackBar.open(snackBarText, '',
          {
            horizontalPosition: 'right',
            verticalPosition: 'bottom',
            duration: 3000,
            panelClass : 'mySnackbar'
          });
    }
  }
}
